import { createFeature, createReducer, on } from '@ngrx/store';

import * as UserAccessManagementActions from './user-access-management.actions';
import {
  LoginSettingTypeEnum,
  IpWhitelistItem,
  IpWhitelistDetail,
} from '../models/user-access-management.model';

import { UserProfileResponse } from '@app/dashboard/models/dashboard.model';

export const userAccessManagementFeatureKey = 'userAccessManagement';

export interface IpWhitelistState {
  items: IpWhitelistItem[];
  selectedDetail: IpWhitelistDetail | null;
  enabled: boolean;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface UserListState {
  users: UserProfileResponse[];
  loading: boolean;
  error: string | null;
}

export interface UserAccessManagementState {
  currentRule: LoginSettingTypeEnum;
  loading: boolean;
  error: string | null;
  ipWhitelist: IpWhitelistState;
  userList: UserListState;
}

const INITIAL_IP_WHITELIST_STATE: IpWhitelistState = {
  items: [],
  selectedDetail: null,
  enabled: false,
  loading: false,
  detailLoading: false,
  error: null,
  currentPage: 0,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
};

const INITIAL_USER_LIST_STATE: UserListState = {
  users: [],
  loading: false,
  error: null,
};

const INITIAL_STATE: UserAccessManagementState = {
  currentRule: LoginSettingTypeEnum.PASSWORD_WITH_OTP,
  loading: false,
  error: null,
  ipWhitelist: INITIAL_IP_WHITELIST_STATE,
  userList: INITIAL_USER_LIST_STATE,
};

/** For tests and components that need the full default slice key (`userAccessManagement`). */
export const userAccessManagementInitialState: UserAccessManagementState =
  INITIAL_STATE;

const reducer = createReducer(
  INITIAL_STATE,
  on(
    UserAccessManagementActions.getLoginSettings,
    (state: UserAccessManagementState) => ({
      ...state,
      loading: true,
      error: null,
    })
  ),
  on(
    UserAccessManagementActions.getLoginSettingsSuccess,
    (state: UserAccessManagementState, { response }) => ({
      ...state,
      loading: false,
      currentRule:
        response?.currentRule ?? LoginSettingTypeEnum.PASSWORD_WITH_OTP,
    })
  ),
  on(
    UserAccessManagementActions.getLoginSettingsFailure,
    (state: UserAccessManagementState, { error }) => ({
      ...state,
      loading: false,
      error,
    })
  ),
  on(
    UserAccessManagementActions.updateLoginSettings,
    (state: UserAccessManagementState) => ({
      ...state,
      loading: true,
      error: null,
    })
  ),
  on(
    UserAccessManagementActions.updateLoginSettingsSuccess,
    (state: UserAccessManagementState) => ({
      ...state,
      loading: true,
      error: null,
      showCancelConfirmationModal: false,
      cancelOrigin: null,
    })
  ),
  on(
    UserAccessManagementActions.updateLoginSettingsSuccess,
    (state: UserAccessManagementState) => ({
      ...state,
      loading: false,
      error: null,
    })
  ),
  on(
    UserAccessManagementActions.updateLoginSettingsFailure,
    (state: UserAccessManagementState, { error }) => ({
      ...state,
      loading: false,
      error,
    })
  ),
  // IP Whitelist Reducers
  on(
    UserAccessManagementActions.setIpWhitelistEnabled,
    (state: UserAccessManagementState, { enabled }) => ({
      ...state,
      ipWhitelist: {
        ...state.ipWhitelist,
        enabled,
      },
    })
  ),
  on(
    UserAccessManagementActions.loadIpWhitelistList,
    (state: UserAccessManagementState) => ({
      ...state,
      ipWhitelist: {
        ...state.ipWhitelist,
        loading: true,
        error: null,
      },
    })
  ),
  on(
    UserAccessManagementActions.loadIpWhitelistListSuccess,
    (state: UserAccessManagementState, { response }) => {
      const pagination = response.pagination;
      return {
        ...state,
        ipWhitelist: {
          ...state.ipWhitelist,
          loading: false,
          items: response.data || [],
          totalItems: pagination?.total || 0,
          totalPages: pagination?.totalPages || 0,
          currentPage: (pagination?.page || 1) - 1, // Convert to 0-based
          pageSize: pagination?.limit || 10,
        },
      };
    }
  ),
  on(
    UserAccessManagementActions.loadIpWhitelistListFailure,
    (state: UserAccessManagementState, { error }) => ({
      ...state,
      ipWhitelist: {
        ...state.ipWhitelist,
        loading: false,
        error,
      },
    })
  ),
  on(
    UserAccessManagementActions.loadIpWhitelistDetail,
    (state: UserAccessManagementState) => ({
      ...state,
      ipWhitelist: {
        ...state.ipWhitelist,
        detailLoading: true,
        error: null,
      },
    })
  ),
  on(
    UserAccessManagementActions.loadIpWhitelistDetailSuccess,
    (state: UserAccessManagementState, { response }) => {
      const data = response?.data;
      return {
        ...state,
        ipWhitelist: {
          ...state.ipWhitelist,
          detailLoading: false,
          // `?? null`: the modal gate opens on `!== null`, so an absent key
          // (undefined) latched it open with nothing to render (F-185).
          selectedDetail: data ?? null,
        },
      };
    }
  ),
  on(
    UserAccessManagementActions.loadIpWhitelistDetailFailure,
    (state: UserAccessManagementState, { error }) => ({
      ...state,
      ipWhitelist: {
        ...state.ipWhitelist,
        detailLoading: false,
        error,
      },
    })
  ),
  on(
    UserAccessManagementActions.clearIpWhitelistDetail,
    (state: UserAccessManagementState) => ({
      ...state,
      ipWhitelist: {
        ...state.ipWhitelist,
        selectedDetail: null,
      },
    })
  ),
  on(
    UserAccessManagementActions.setIpWhitelistPage,
    (state: UserAccessManagementState, { pageIndex, pageSize }) => ({
      ...state,
      ipWhitelist: {
        ...state.ipWhitelist,
        currentPage: pageIndex,
        pageSize,
      },
    })
  ),
  on(
    UserAccessManagementActions.createIpRestriction,
    (state: UserAccessManagementState) => ({
      ...state,
      loading: true,
      error: null,
    })
  ),
  on(
    UserAccessManagementActions.createIpRestrictionSuccess,
    (state: UserAccessManagementState) => ({
      ...state,
      loading: false,
      error: null,
    })
  ),
  on(
    UserAccessManagementActions.createIpRestrictionFailure,
    (state: UserAccessManagementState, { error }) => ({
      ...state,
      loading: false,
      error,
    })
  ),
  // User List Reducers
  on(
    UserAccessManagementActions.loadUserList,
    (state: UserAccessManagementState) => ({
      ...state,
      userList: {
        ...state.userList,
        loading: true,
        error: null,
      },
    })
  ),
  on(
    UserAccessManagementActions.loadUserListSuccess,
    (state: UserAccessManagementState, { response }) => {
      const users = Array.isArray(response) ? response : (response?.data ?? []);
      return {
        ...state,
        userList: {
          ...state.userList,
          loading: false,
          users,
        },
      };
    }
  ),
  on(
    UserAccessManagementActions.loadUserListFailure,
    (state: UserAccessManagementState, { error }) => ({
      ...state,
      userList: {
        ...state.userList,
        loading: false,
        error,
      },
    })
  )
);

export const userAccessManagementFeature = createFeature({
  name: userAccessManagementFeatureKey,
  reducer,
});

export const {
  selectUserAccessManagementState,
  selectError,
  selectCurrentRule,
  selectLoading,
  selectIpWhitelist,
} = userAccessManagementFeature;
